import React, { useEffect, useState } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import ExportButton from '../components/ExportButton';
import PermissionButton from '../components/PermissionButton';
import { useAddCityMutation, useDeleteCityMutation, useGetActiveCountriesQuery, useGetCitiesQuery, useUpdateCityMutation, useAddCountryMutation } from '../store/api';
import { toast } from 'react-toastify';
import TableHeader from '../components/TableHeader';
import CityViewModal from '../components/modals/CityViewModal';
import { Audio } from 'react-loader-spinner'
import { usePermission } from '../hooks/useAuth';

export default function Cities() {
  const [searchTerm, setSearchTerm] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [isExport, setIsExport] = useState(0)
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  const [viewingCity, setViewingCity] = useState(null);
  const [activeTab, setActiveTab] = useState('universities');
  
  // Permission hooks - strictly permission-based
  const canAddCity = usePermission('city.add');
  const canEditCity = usePermission('city.edit');
  const canDeleteCity = usePermission('city.delete');
  const canViewCity = usePermission('city.view'); // Already being used in route protection
  
  const [formData, setFormData] = useState({ name: '', country_id: '', is_active: 1 });
  const [citiesData, setCitiesData] = useState(null)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCityCount, setTotalCityCount] = useState(0)
  const [exportData, setExportData] = useState([])
  const [activeCountries, setActiveCountries] = useState([])

  // New country form states
  const [showAddCountryForm, setShowAddCountryForm] = useState(false);
  const [newCountryData, setNewCountryData] = useState({ name: '', code: '', is_active: 1 });

  const [deletingCityId,setDeletingCityId] = useState(null)
  const { data, isError, error, isSuccess, isLoading, refetch } = useGetCitiesQuery({
    keyword: searchTerm,
    country_id: countryFilter,
    is_active: statusFilter,
    page: currentPage,
    limit: itemsPerPage,
    is_export: isExport
  })

  const { data : countryData, isError: countryIsError, error: countryError, isSuccess: countryIsSuccess, isLoading:countryIsLoading, refetch: countryRefetch } = useGetActiveCountriesQuery({
    is_active: 1
  })


  useEffect(() => {
    if(!countryIsLoading && countryData){
      setActiveCountries(countryData.data)
    }
  },[countryIsLoading,countryData])



  const [updateCity, {
    data: updateData,
    isError: updateIsError,
    error: updateError,
    isSuccess: updateIsSuccess,
    isLoading: updateIsLoading }] = useUpdateCityMutation()

    const [addCity, {
      data: createResponse,
      isError: createIsError,
      error: createError,
      isSuccess: createIsSuccess,
      isLoading: createIsLoading }] = useAddCityMutation()

    const [deleteCity,{
      data: deleteRes,
      isError: deleteIsError,
      error: deleteError,
      isSuccess: deleteIsSuccess,
      isLoading: deleteIsLoading
    }] = useDeleteCityMutation() 

    // Add Country Mutation
    const [addCountry, {
      data: countryCreateResponse,
      isError: countryCreateIsError,
      error: countryCreateError,
      isSuccess: countryCreateIsSuccess,
      isLoading: countryCreateIsLoading
    }] = useAddCountryMutation()

  
    useEffect(() => {
      if(!deleteIsLoading && deleteRes && deleteIsSuccess){
        toast.success(deleteRes.message);
      }
    }, [deleteIsLoading, deleteRes, deleteIsSuccess])

  useEffect(() => {
    if(!createIsLoading && createResponse){
      toast.success(createResponse.message);
      setIsModalOpen(false);
      setFormData({ name: '', country_id: '', is_active: 0 });
    }
  }, [createResponse, createIsLoading])    

  useEffect(() => {

    if (!updateIsLoading && updateData) {
      toast.success(updateData.message)
      setIsModalOpen(false);
      setEditingCity(null);
      setFormData({ name: '', country_id: '', is_active: 1 });
    }

    if (updateIsError) {
      toast.error("Yeniləmə uğursuz oldu")
      console.log(updateError);
    }
  }, [updateData, updateIsSuccess, updateIsError, updateIsLoading, updateError])

  // Country create useEffect
  useEffect(() => {
    if (!countryCreateIsLoading && countryCreateResponse) {
      toast.success(countryCreateResponse.message);
      setShowAddCountryForm(false);
      setNewCountryData({ name: '', code: '', is_active: 1 });
      // Refresh countries list and select the new country
      countryRefetch().then((result) => {
        if (result.data) {
          const newCountry = result.data.data.find(country => country.name === countryCreateResponse.data.name);
          if (newCountry) {
            setFormData(prev => ({ ...prev, country_id: newCountry.id }));
          }
        }
      });
    }

    if (countryCreateIsError) {
      toast.error(countryCreateError?.data?.message || 'Ölkə əlavə etmə uğursuz oldu');
    }
  }, [countryCreateResponse, countryCreateIsSuccess, countryCreateIsError, countryCreateIsLoading, countryCreateError, countryRefetch])

  useEffect(() => {
    if (!isLoading && data) {
      if (isExport === 1) {

        const transformedData = data.data.map(city => ({
          'Şəhər Adı': city.name,
          'Ölkə': city.country.name,
          'Status': city.is_active ? 'Aktiv' : 'Deaktiv',
          'Yaradılma Tarixi': new Date(city.created_at).toLocaleString("az-AZ"),
          'ID': city.id
        }));

        setExportData(transformedData)

      } else {
        setCitiesData(data.data.data)
        setTotalPages(Math.ceil(data.data.total / data.data.per_page))
        setTotalCityCount(data.data.total)

      }
    }
  }, [data, isSuccess, isError, isLoading, error])

  // Pagination calculations
  const startIndex = (currentPage - 1) * itemsPerPage;

  // Export data preparation

  const exportHeaders = ['Şəhər Adı', 'Ölkə', 'Status', 'Yaradılma Tarixi', 'ID'];



  const handleAdd = () => {
    setEditingCity(null);
    setFormData({ name: '', country_id: '', is_active: true });
    setIsModalOpen(true);
  };

  const handleEdit = (city) => {
    setEditingCity(city);
    setFormData({ name: city.name, country_id: city.country_id, is_active: city.is_active });
    setIsModalOpen(true);
  };
  

  const handleView = (city) => {
    setViewingCity(city);
    setActiveTab('universities');
    setIsViewModalOpen(true);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setCountryFilter('');
    setStatusFilter('');
  };

  const handleDelete = (cityId) => {
    if (window.confirm('Bu şəhəri silmək istədiyinizə əminsiniz?')) {
      deleteCity(cityId)
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // console.log('Saving city:', formData);
    // console.log(editingCity.id)
    // return
    if(editingCity){
      updateCity({id: editingCity?.id,obj:formData})
    }else{
      addCity(formData);
    }
  };

  // Country handlers
  const handleAddCountryClick = () => {
    setShowAddCountryForm(true);
  };

  const handleCountrySubmit = (e) => {
    e.preventDefault();
    addCountry(newCountryData);
  };

  const handleCancelAddCountry = () => {
    setShowAddCountryForm(false);
    setNewCountryData({ name: '', code: '', is_active: 1 });
  };

  if (isLoading) {
          return (
              <div className='fixed inset-0 flex items-center bg-white opacity-[.5] justify-center'>
                  <Audio
                      height="80"
                      width="80"
                      radius="9"
                      color="green"
                      ariaLabel="loading"
                      wrapperStyle
                      wrapperClass
                  />
              </div>
          )
      }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Şəhərlər</h1>
          <p className="mt-1 text-sm text-gray-600">
            Ölkələr üzrə şəhərləri idarə edin
          </p>
          <div className="mt-2 flex items-center space-x-4">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200">
              Ümumi: {totalCityCount}
            </span>
            {(searchTerm || countryFilter || statusFilter) && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200">
                Tapılan: { totalCityCount }
              </span>
            )}
          </div>
        </div>
        <div className="mt-4 sm:mt-0 sm:flex sm:space-x-3">
          <ExportButton
            setIsExport={setIsExport}
            data={exportData}
            filename="sehirler"
            headers={exportHeaders}
          />
          {canAddCity && (
            <button
              onClick={handleAdd}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Yeni Şəhər
            </button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 max-w-6xl">
        <div className="md:col-span-2">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Şəhər və ya ölkə adı ilə axtarış..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div>
          <select
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Bütün ölkələr</option>
            {!countryIsLoading && activeCountries.length && activeCountries.map(country => (
              <option key={country.id} value={country.id}>{country.name}</option>
            ))}
          </select>
        </div>
        <div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Bütün statuslar</option>
            <option value="1">Yalnız aktiv</option>
            <option value="0">Yalnız deaktiv</option>
          </select>
        </div>
        <div>
          <button
            onClick={handleClearFilters}
            disabled={!searchTerm && !countryFilter && !statusFilter}
            className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Filtri təmizlə
          </button>
        </div>
      </div>

      {/* Cities table */}
      <div className="bg-white relative shadow-lg  sm:rounded-xl border border-blue-100">
        {/* Table header with pagination controls */}

        <TableHeader
          currentPage={currentPage}
          setItemsPerPage={setItemsPerPage}
          itemsPerPage={itemsPerPage}
          setCurrentPage={setCurrentPage}
          startIndex={startIndex}
          title={"Şəhərlər"}
          totalPages={totalPages}
          totalRecordCount={totalCityCount}
        />

        <div className="px-6 py-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-blue-50 to-blue-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    <div className="flex items-center space-x-2">
                      <span>Şəhər Adı</span>
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    <div className="flex items-center space-x-2">
                      <span>Ölkə</span>
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    <div className="flex items-center space-x-2">
                      <span>Status</span>
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    <div className="flex items-center space-x-2">
                      <span>Yaradılma Tarixi</span>
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Əməliyyatlar
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {!isLoading && citiesData?.length > 0 && citiesData.map((city, index) => (
                  <tr key={city.id} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 transition-all duration-200 group">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`flex-shrink-0 h-3 w-3 rounded-full mr-3 transition-colors ${city.is_active ? 'bg-blue-600 group-hover:bg-blue-700' : 'bg-gray-400 group-hover:bg-gray-500'
                          }`}></div>
                        <div className={`text-sm font-semibold transition-colors ${city.is_active ? 'text-gray-900 group-hover:text-blue-900' : 'text-gray-500 group-hover:text-gray-600'
                          }`}>
                          {city.name}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                        {city.country.name}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${city.is_active
                        ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                        : 'bg-red-100 text-red-800 border border-red-200'
                        }`}>
                        {city.is_active ? 'Aktiv' : 'Deaktiv'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600 bg-blue-50 px-3 py-1 rounded-md border border-blue-100">
                        {new Date(city.created_at).toLocaleDateString('az-AZ')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex justify-end space-x-2">
                        {canViewCity && (
                          <button
                            onClick={() => handleView(city)}
                            className="inline-flex items-center p-2 bg-green-100 text-green-600 hover:bg-green-200 hover:text-green-800 rounded-lg transition-all duration-200 hover:scale-105"
                            title="Ətraflı bax"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        )}
                        {canEditCity && (
                          <button
                            onClick={() => handleEdit(city)}
                            className="inline-flex items-center p-2 bg-blue-100 text-blue-600 hover:bg-blue-200 hover:text-blue-800 rounded-lg transition-all duration-200 hover:scale-105"
                            title="Redaktə et"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        )}
                        {canDeleteCity && (
                          <button
                            onClick={() => handleDelete(city.id)}
                            className="inline-flex items-center p-2 bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-800 rounded-lg transition-all duration-200 hover:scale-105"
                            title="Sil"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!isLoading && citiesData?.length === 0 && (
            <div className="text-center py-16">
              <div className="mx-auto h-16 w-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mb-4">
                <svg className="h-8 w-8 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Şəhər tapılmadı</h3>
              <p className="text-sm text-gray-500 max-w-sm mx-auto">
                Axtarış şərtlərinizi dəyişin və ya yeni şəhər əlavə edin.
              </p>
              {canAddCity && (
                <button
                  onClick={handleAdd}
                  className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  İlk Şəhəri Əlavə Et
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setIsModalOpen(false)} />

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingCity ? 'Şəhəri Redaktə Et' : 'Yeni Şəhər Əlavə Et'}
                </h3>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Şəhər Adı *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Şəhər adını daxil edin"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Ölkə *
                    </label>
                    {!showAddCountryForm && (
                      <button
                        type="button"
                        onClick={handleAddCountryClick}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-100 border border-blue-200 rounded-md hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        title="Yeni ölkə əlavə et"
                      >
                        <PlusIcon className="h-3 w-3 mr-1" />
                        Yeni Ölkə
                      </button>
                    )}
                  </div>
                  
                  {!showAddCountryForm ? (
                    <select
                      required
                      value={formData.country_id}
                      onChange={(e) => setFormData({ ...formData, country_id: e.target.value })}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Seçin</option>
                      {!countryIsLoading && activeCountries.length && activeCountries.map(country => (
                        <option key={country.id} value={country.id}>{country.name}</option>
                      ))}
                    </select>
                  ) : (
                    <div className="space-y-3 p-3 border border-blue-200 rounded-md bg-blue-50">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-blue-900">Yeni Ölkə Əlavə Et</h4>
                        <button
                          type="button"
                          onClick={handleCancelAddCountry}
                          className="text-blue-400 hover:text-blue-600"
                        >
                          <span className="text-lg">&times;</span>
                        </button>
                      </div>
                      
                      <form onSubmit={handleCountrySubmit} className="space-y-2">
                        <div>
                          <input
                            type="text"
                            placeholder="Ölkə adı *"
                            required
                            value={newCountryData.name}
                            onChange={(e) => setNewCountryData({ ...newCountryData, name: e.target.value })}
                            className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <input
                            type="text"
                            placeholder="Ölkə kodu (məs: AZ) *"
                            required
                            value={newCountryData.code}
                            onChange={(e) => setNewCountryData({ ...newCountryData, code: e.target.value.toUpperCase() })}
                            className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            maxLength="3"
                          />
                        </div>
                        <div className="flex justify-end space-x-2">
                          <button
                            type="button"
                            onClick={handleCancelAddCountry}
                            className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                          >
                            Ləğv et
                          </button>
                          <button
                            type="submit"
                            disabled={countryCreateIsLoading}
                            className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
                          >
                            {countryCreateIsLoading ? 'Əlavə edilir...' : 'Əlavə et'}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <div className="mt-2">
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })}
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        Şəhər aktiv statusdadır
                      </span>
                    </label>
                    <p className="mt-1 text-xs text-gray-500">
                      Deaktiv şəhərlər yeni müraciətlərdə göstərilməz
                    </p>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Ləğv et
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    {editingCity ? 'Yenilə' : 'Yadda saxla'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {isViewModalOpen && viewingCity && (
        // <div className="fixed inset-0 z-50 overflow-y-auto">
        //   <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        //     <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setIsViewModalOpen(false)} />

        //     <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full sm:p-6">
        //       <div className="flex items-center justify-between mb-6">
        //         <div className="flex items-center space-x-3">
        //           <div className={`h-3 w-3 rounded-full ${viewingCity.is_active ? 'bg-green-500' : 'bg-red-500'}`}></div>
        //           <h3 className="text-lg font-medium text-gray-900">
        //             {viewingCity.name} - Ətraflı Məlumat
        //           </h3>
        //           <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
        //             {viewingCity.country.name}
        //           </span>
        //         </div>
        //         <button
        //           onClick={() => setIsViewModalOpen(false)}
        //           className="text-gray-400 hover:text-gray-600"
        //         >
        //           <span className="text-2xl">&times;</span>
        //         </button>
        //       </div>

        //       {/* City details */}
        //       <div className="mb-6 bg-blue-50 rounded-lg p-4 border border-blue-200">
        //         <h4 className="font-medium text-blue-900 mb-3">Şəhər Məlumatları</h4>
        //         <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        //           <div>
        //             <div className="text-sm font-medium text-gray-500">Şəhər Adı</div>
        //             <div className="text-sm text-gray-900">{viewingCity.name}</div>
        //           </div>
        //           <div>
        //             <div className="text-sm font-medium text-gray-500">Ölkə</div>
        //             <div className="text-sm text-gray-900">{viewingCity.country.name}</div>
        //           </div>
        //           <div>
        //             <div className="text-sm font-medium text-gray-500">Status</div>
        //             <div className="text-sm text-gray-900">
        //               {viewingCity.is_active ? 'Aktiv' : 'Deaktiv'}
        //             </div>
        //           </div>
        //           <div>
        //             <div className="text-sm font-medium text-gray-500">Yaradılma Tarixi</div>
        //             <div className="text-sm text-gray-900">{viewingCity.created_at}</div>
        //           </div>
        //         </div>
        //       </div>

        //       {/* Tabs */}
        //       <div className="border-b border-gray-200 mb-6">
        //         <nav className="-mb-px flex space-x-8">
        //           <button
        //             onClick={() => setActiveTab('universities')}
        //             className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'universities'
        //               ? 'border-blue-500 text-blue-600'
        //               : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
        //               }`}
        //           >
        //             Universitetlər ({getCityUniversities(viewingCity.name).length})
        //           </button>
        //           <button
        //             onClick={() => setActiveTab('applications')}
        //             className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'applications'
        //               ? 'border-blue-500 text-blue-600'
        //               : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
        //               }`}
        //           >
        //             Müraciətlər ({getCityStudents(viewingCity.name).length})
        //           </button>
        //         </nav>
        //       </div>

        //       {/* Tab Content */}
        //       <div className="min-h-64">
        //         {activeTab === 'universities' && (
        //           <div className="space-y-3">
        //             {getCityUniversities(viewingCity.name).length > 0 ? (
        //               getCityUniversities(viewingCity.name).map((uni) => (
        //                 <div key={uni.id} className="bg-white p-4 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors">
        //                   <div className="flex items-center justify-between">
        //                     <div className="flex items-center space-x-3">
        //                       <div className="h-3 w-3 bg-blue-600 rounded-full"></div>
        //                       <div>
        //                         <div className="font-medium text-gray-900">{uni.name}</div>
        //                         <div className="text-sm text-gray-500">{uni.country}</div>
        //                       </div>
        //                     </div>
        //                     <div className="text-right">
        //                       <div className="text-xs text-blue-600 font-medium">Reytinq #{uni.ranking}</div>
        //                     </div>
        //                   </div>
        //                 </div>
        //               ))
        //             ) : (
        //               <div className="text-center py-8">
        //                 <div className="mx-auto h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
        //                   <svg className="h-6 w-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        //                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        //                   </svg>
        //                 </div>
        //                 <h5 className="text-lg font-medium text-gray-900 mb-2">Universitet tapılmadı</h5>
        //                 <p className="text-gray-500">Bu şəhərdə hələ universitet qeydiyyatda deyil</p>
        //               </div>
        //             )}
        //           </div>
        //         )}

        //         {activeTab === 'applications' && (
        //           <div className="space-y-3">
        //             {getCityStudents(viewingCity.name).length > 0 ? (
        //               getCityStudents(viewingCity.name).map((student) => (
        //                 <div key={student.id} className="bg-white p-4 rounded-lg border border-gray-200 hover:border-green-300 transition-colors">
        //                   <div className="flex items-center justify-between">
        //                     <div className="flex items-center space-x-3">
        //                       <div className="h-10 w-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
        //                         <span className="text-xs font-bold text-white">
        //                           {student.firstName[0]}{student.lastName[0]}
        //                         </span>
        //                       </div>
        //                       <div>
        //                         <div className="font-medium text-gray-900">
        //                           {student.firstName} {student.lastName}
        //                         </div>
        //                         <div className="text-sm text-gray-500">{student.phone}</div>
        //                         <div className="text-xs text-gray-400">
        //                           Universitetlər: {student.appliedUniversities}
        //                         </div>
        //                       </div>
        //                     </div>
        //                     <div className="text-right">
        //                       <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
        //                         {student.status}
        //                       </span>
        //                     </div>
        //                   </div>
        //                 </div>
        //               ))
        //             ) : (
        //               <div className="text-center py-8">
        //                 <div className="mx-auto h-12 w-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
        //                   <svg className="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        //                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
        //                   </svg>
        //                 </div>
        //                 <h5 className="text-lg font-medium text-gray-900 mb-2">Müraciət tapılmadı</h5>
        //                 <p className="text-gray-500">Bu şəhərdəki universitetlərə hələ heç bir müraciət yoxdur</p>
        //               </div>
        //             )}
        //           </div>
        //         )}
        //       </div>

        //       <div className="mt-6 flex justify-end space-x-3">
        //         <button
        //           onClick={() => {
        //             setIsViewModalOpen(false);
        //             handleEdit(viewingCity);
        //           }}
        //           className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        //         >
        //           <PencilIcon className="-ml-1 mr-2 h-4 w-4" />
        //           Redaktə Et
        //         </button>
        //       </div>
        //     </div>
        //   </div>
        // </div>
        <CityViewModal
          setIsViewModalOpen={setIsViewModalOpen}
          viewingCity={viewingCity}
          handleEdit={handleEdit}
        />
      )}
    </div>
  );
} 